/**
 * Reliability Model - Phase 14
 * 
 * Metrics model for merchant/location/terminal reliability reporting.
 * Tracks offline incidents, sync success rates, and recovery performance.
 */

// ==================== RELIABILITY STATUS ====================

export const RELIABILITY_STATUS = {
  HEALTHY: 'healthy',
  WATCH: 'watch',
  AT_RISK: 'at_risk',
  CRITICAL: 'critical'
};

export const RELIABILITY_STATUS_CONFIG = {
  [RELIABILITY_STATUS.HEALTHY]: {
    label: 'Healthy',
    color: 'green',
    description: 'Low incident volume, no critical issues'
  },
  [RELIABILITY_STATUS.WATCH]: {
    label: 'Watch',
    color: 'yellow',
    description: 'Some open incidents, limited stale issues'
  },
  [RELIABILITY_STATUS.AT_RISK]: {
    label: 'At Risk',
    color: 'orange',
    description: 'Repeated failures, stale blocked payments'
  },
  [RELIABILITY_STATUS.CRITICAL]: {
    label: 'Critical',
    color: 'red',
    description: 'Critical open incidents or sustained degraded state'
  }
};

// ==================== RELIABILITY SUMMARY SHAPE ====================

/**
 * @typedef {Object} ReliabilitySummary
 * @property {string} entityType - merchant | location | terminal
 * @property {string} entityId
 * @property {string} entityName
 * @property {string} reliabilityStatus
 * @property {number} reliabilityScore - 0-100
 * @property {Object} core - Core metrics
 * @property {Object} pos - POS metrics
 * @property {Object} staff - Staff/BackOffice metrics
 * @property {Object} trends - Trend data
 * @property {number} computedAt
 */

/**
 * Create empty reliability summary
 * @param {string} entityType
 * @param {string} entityId
 * @param {string} entityName
 * @returns {ReliabilitySummary}
 */
export function createEmptyReliabilitySummary(entityType, entityId, entityName = '') {
  return {
    entityType,
    entityId,
    entityName,
    reliabilityStatus: RELIABILITY_STATUS.HEALTHY,
    reliabilityScore: 100,
    core: {
      offlineIncidentCount: 0,
      unresolvedIncidentCount: 0,
      criticalIncidentCount: 0,
      blockedPaymentCount: 0,
      syncSuccessRate: 100,
      syncFailureRate: 0,
      replayRetryRate: 0,
      conflictRate: 0,
      averageResolutionTimeMs: 0,
      oldestUnresolvedAgeMs: 0,
      affectedTerminalsCount: 0,
      affectedLocationsCount: 0
    },
    pos: {
      unsyncedOrdersCount: 0,
      paymentRecoverySuccessRate: 100,
      orderReplaySuccessRate: 100,
      failedKitchenCount: 0,
      failedReceiptsCount: 0,
      blockedPaymentRecoveryRate: 0
    },
    staff: {
      offlineStaffActionsCount: 0,
      availabilityReplaySuccessRate: 100,
      scheduleAckReplaySuccessRate: 100,
      backofficeReplaySuccessRate: 100
    },
    trends: {
      incidentsOverTime: [],
      blockedPaymentsOverTime: [],
      failedSyncsOverTime: [],
      repeatedIssueLocations: [],
      repeatedIssueTerminals: []
    },
    computedAt: Date.now()
  };
}

// ==================== METRICS COMPUTATION ====================

/**
 * Build reliability summary from raw data
 * @param {Object} options
 * @returns {ReliabilitySummary}
 */
export function buildReliabilitySummary(options) {
  const {
    entityType,
    entityId,
    entityName = '',
    incidents = [],
    queueItems = [],
    syncLogs = [],
    dateFrom = null,
    dateTo = null
  } = options;
  
  const summary = createEmptyReliabilitySummary(entityType, entityId, entityName);
  
  // Apply date filters
  const filterByDate = (items, dateField = 'occurredAt') => {
    let filtered = items;
    if (dateFrom) filtered = filtered.filter(i => i[dateField] >= dateFrom);
    if (dateTo) filtered = filtered.filter(i => i[dateField] <= dateTo);
    return filtered;
  };
  
  const filteredIncidents = filterByDate(incidents);
  const filteredLogs = filterByDate(syncLogs, 'syncedAt');
  
  // Compute core metrics
  summary.core = computeCoreMetrics(filteredIncidents, filteredLogs, queueItems);
  
  // Compute POS metrics
  summary.pos = computePosMetrics(filteredIncidents, queueItems, filteredLogs);
  
  // Compute staff metrics
  summary.staff = computeStaffMetrics(filteredIncidents, filteredLogs);
  
  // Compute trends
  summary.trends = computeTrends(filteredIncidents, filteredLogs);
  
  // Compute overall status and score
  const { status, score } = computeReliabilityStatus(summary);
  summary.reliabilityStatus = status;
  summary.reliabilityScore = score;
  
  summary.computedAt = Date.now();
  
  return summary;
}

/**
 * Compute core reliability metrics
 * @param {Array} incidents
 * @param {Array} syncLogs
 * @param {Array} queueItems
 * @returns {Object}
 */
function computeCoreMetrics(incidents, syncLogs, queueItems) {
  const metrics = {
    offlineIncidentCount: incidents.length,
    unresolvedIncidentCount: 0,
    criticalIncidentCount: 0,
    blockedPaymentCount: 0,
    syncSuccessRate: 0,
    syncFailureRate: 0,
    replayRetryRate: 0,
    conflictRate: 0,
    averageResolutionTimeMs: 0,
    oldestUnresolvedAgeMs: 0,
    affectedTerminalsCount: 0,
    affectedLocationsCount: 0
  };
  
  // Count incident types
  const openStatuses = ['observed', 'queued', 'retrying', 'failed', 'conflict', 'manual_review'];
  const unresolved = incidents.filter(i => openStatuses.includes(i.status));
  metrics.unresolvedIncidentCount = unresolved.length;
  metrics.criticalIncidentCount = incidents.filter(i => i.severity === 'critical').length;
  metrics.blockedPaymentCount = incidents.filter(i => 
    i.domain === 'payments' && (i.status === 'failed' || i.status === 'conflict')
  ).length;
  
  // Sync rates
  const totalSyncs = syncLogs.length;
  if (totalSyncs > 0) {
    const success = syncLogs.filter(l => l.result === 'success').length;
    const failed = syncLogs.filter(l => l.result === 'failed').length;
    const conflict = syncLogs.filter(l => l.result === 'conflict').length;
    const retry = syncLogs.filter(l => l.result === 'retry').length;
    
    metrics.syncSuccessRate = (success / totalSyncs * 100);
    metrics.syncFailureRate = (failed / totalSyncs * 100);
    metrics.conflictRate = (conflict / totalSyncs * 100);
    metrics.replayRetryRate = (retry / totalSyncs * 100);
  } else {
    metrics.syncSuccessRate = 100;
  }
  
  // Resolution time
  const resolvedIncidents = incidents.filter(i => i.resolvedAt && i.occurredAt);
  if (resolvedIncidents.length > 0) {
    const totalTime = resolvedIncidents.reduce((sum, i) => sum + (i.resolvedAt - i.occurredAt), 0);
    metrics.averageResolutionTimeMs = Math.round(totalTime / resolvedIncidents.length);
  }
  
  // Oldest unresolved
  if (unresolved.length > 0) {
    const oldest = unresolved.reduce((oldest, i) => 
      i.occurredAt < oldest.occurredAt ? i : oldest
    , unresolved[0]);
    metrics.oldestUnresolvedAgeMs = Date.now() - oldest.occurredAt;
  }
  
  // Affected entities
  const terminals = new Set(incidents.filter(i => i.terminalId).map(i => i.terminalId));
  const locations = new Set(incidents.filter(i => i.locationId).map(i => i.locationId));
  metrics.affectedTerminalsCount = terminals.size;
  metrics.affectedLocationsCount = locations.size;
  
  return metrics;
}

/**
 * Compute POS-specific metrics
 * @param {Array} incidents
 * @param {Array} queueItems
 * @param {Array} syncLogs
 * @returns {Object}
 */
function computePosMetrics(incidents, queueItems, syncLogs) {
  const metrics = {
    unsyncedOrdersCount: 0,
    paymentRecoverySuccessRate: 100,
    orderReplaySuccessRate: 100,
    failedKitchenCount: 0,
    failedReceiptsCount: 0,
    blockedPaymentRecoveryRate: 0
  };
  
  // POS queue items
  const posOrderTypes = ['POS_ORDER_CREATE', 'POS_ORDER_UPDATE', 'POS_ORDER_CLOSE'];
  const posPaymentTypes = ['POS_PAYMENT_CASH', 'POS_PAYMENT_CARD'];
  
  const orderItems = queueItems.filter(i => posOrderTypes.includes(i.type));
  const paymentItems = queueItems.filter(i => posPaymentTypes.includes(i.type));
  
  metrics.unsyncedOrdersCount = orderItems.filter(i => i.status !== 'completed').length;
  
  // Order replay success rate
  const orderLogs = syncLogs.filter(l => posOrderTypes.includes(l.type));
  if (orderLogs.length > 0) {
    const success = orderLogs.filter(l => l.result === 'success').length;
    metrics.orderReplaySuccessRate = (success / orderLogs.length * 100);
  }
  
  // Payment recovery success rate
  const paymentLogs = syncLogs.filter(l => posPaymentTypes.includes(l.type));
  if (paymentLogs.length > 0) {
    const success = paymentLogs.filter(l => l.result === 'success').length;
    metrics.paymentRecoverySuccessRate = (success / paymentLogs.length * 100);
  }
  
  // Failed kitchen/receipts
  metrics.failedKitchenCount = queueItems.filter(i => 
    i.type === 'POS_KITCHEN_SEND' && i.status === 'failed'
  ).length;
  
  metrics.failedReceiptsCount = queueItems.filter(i => 
    i.type === 'POS_RECEIPT_PRINT' && i.status === 'failed'
  ).length;
  
  // Blocked payment recovery rate
  const blockedPayments = paymentItems.filter(i => i.status === 'failed' || i.status === 'conflict');
  const recoveredPayments = paymentLogs.filter(l => l.result === 'success').length;
  if (blockedPayments.length > 0) {
    metrics.blockedPaymentRecoveryRate = (recoveredPayments / blockedPayments.length * 100);
  }
  
  return metrics;
}

/**
 * Compute staff/backoffice metrics
 * @param {Array} incidents
 * @param {Array} syncLogs
 * @returns {Object}
 */
function computeStaffMetrics(incidents, syncLogs) {
  const metrics = {
    offlineStaffActionsCount: 0,
    availabilityReplaySuccessRate: 100,
    scheduleAckReplaySuccessRate: 100,
    backofficeReplaySuccessRate: 100
  };
  
  const staffIncidents = incidents.filter(i => i.sourceType === 'staff');
  metrics.offlineStaffActionsCount = staffIncidents.length;
  
  // Availability replay
  const availLogs = syncLogs.filter(l => l.type === 'AVAILABILITY_UPDATE');
  if (availLogs.length > 0) {
    const success = availLogs.filter(l => l.result === 'success').length;
    metrics.availabilityReplaySuccessRate = (success / availLogs.length * 100);
  }
  
  // Schedule ack replay
  const schedLogs = syncLogs.filter(l => l.type === 'SCHEDULE_ACK');
  if (schedLogs.length > 0) {
    const success = schedLogs.filter(l => l.result === 'success').length;
    metrics.scheduleAckReplaySuccessRate = (success / schedLogs.length * 100);
  }
  
  // BackOffice replay (all non-POS, non-staff types)
  const boTypes = ['BUSINESS_PROFILE_UPDATE', 'LOCATION_HOURS_UPDATE', 'STAFF_PROFILE_UPDATE'];
  const boLogs = syncLogs.filter(l => boTypes.includes(l.type));
  if (boLogs.length > 0) {
    const success = boLogs.filter(l => l.result === 'success').length;
    metrics.backofficeReplaySuccessRate = (success / boLogs.length * 100);
  }
  
  return metrics;
}

/**
 * Compute trend data
 * @param {Array} incidents
 * @param {Array} syncLogs
 * @returns {Object}
 */
function computeTrends(incidents, syncLogs) {
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  
  // Group incidents by day for last 7 days
  const incidentsOverTime = [];
  const blockedPaymentsOverTime = [];
  const failedSyncsOverTime = [];
  
  for (let i = 6; i >= 0; i--) {
    const dayStart = now - (i * dayMs);
    const dayEnd = dayStart + dayMs;
    
    const dayIncidents = incidents.filter(inc => 
      inc.occurredAt >= dayStart && inc.occurredAt < dayEnd
    ).length;
    
    const dayBlocked = incidents.filter(inc => 
      inc.occurredAt >= dayStart && inc.occurredAt < dayEnd &&
      inc.domain === 'payments' && (inc.status === 'failed' || inc.status === 'conflict')
    ).length;
    
    const dayFailed = syncLogs.filter(log => 
      log.syncedAt >= dayStart && log.syncedAt < dayEnd &&
      log.result === 'failed'
    ).length;
    
    const date = new Date(dayStart).toISOString().slice(0, 10);
    incidentsOverTime.push({ date, count: dayIncidents });
    blockedPaymentsOverTime.push({ date, count: dayBlocked });
    failedSyncsOverTime.push({ date, count: dayFailed });
  }
  
  // Find repeated issue entities
  const locationCounts = {};
  const terminalCounts = {};
  
  for (const inc of incidents) {
    if (inc.locationId) {
      locationCounts[inc.locationId] = (locationCounts[inc.locationId] || 0) + 1;
    }
    if (inc.terminalId) {
      terminalCounts[inc.terminalId] = (terminalCounts[inc.terminalId] || 0) + 1;
    }
  }
  
  const repeatedIssueLocations = Object.entries(locationCounts)
    .filter(([_, count]) => count >= 3)
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  
  const repeatedIssueTerminals = Object.entries(terminalCounts)
    .filter(([_, count]) => count >= 3)
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  
  return {
    incidentsOverTime,
    blockedPaymentsOverTime,
    failedSyncsOverTime,
    repeatedIssueLocations,
    repeatedIssueTerminals
  };
}

/**
 * Compute reliability status and score
 * @param {ReliabilitySummary} summary
 * @returns {{ status: string, score: number }}
 */
export function computeReliabilityStatus(summary) {
  let score = 100;
  
  // Deduct for critical incidents
  score -= summary.core.criticalIncidentCount * 15;
  
  // Deduct for unresolved incidents
  score -= Math.min(summary.core.unresolvedIncidentCount * 3, 20);
  
  // Deduct for blocked payments
  score -= summary.core.blockedPaymentCount * 10;
  
  // Deduct for low sync success rate
  if (summary.core.syncSuccessRate < 95) {
    score -= (95 - summary.core.syncSuccessRate);
  }
  
  // Deduct for stale issues (older than 24h)
  const dayMs = 24 * 60 * 60 * 1000;
  if (summary.core.oldestUnresolvedAgeMs > dayMs) {
    score -= 10;
  }
  
  // Deduct for multiple affected terminals
  if (summary.core.affectedTerminalsCount > 3) {
    score -= 5;
  }
  
  // Ensure score is in valid range
  score = Math.max(0, Math.min(100, score));
  
  // Determine status based on score and critical indicators
  let status = RELIABILITY_STATUS.HEALTHY;
  
  if (summary.core.criticalIncidentCount > 0 || score < 40) {
    status = RELIABILITY_STATUS.CRITICAL;
  } else if (summary.core.blockedPaymentCount > 0 || score < 60) {
    status = RELIABILITY_STATUS.AT_RISK;
  } else if (summary.core.unresolvedIncidentCount > 5 || score < 80) {
    status = RELIABILITY_STATUS.WATCH;
  }
  
  return { status, score: Math.round(score) };
}

/**
 * Compute average resolution time formatted
 * @param {number} ms
 * @returns {string}
 */
export function computeAverageResolutionTime(ms) {
  if (!ms || ms === 0) return 'N/A';
  
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  if (ms < 86400000) return `${(ms / 3600000).toFixed(1)}h`;
  return `${(ms / 86400000).toFixed(1)}d`;
}

/**
 * Compute success rates object
 * @param {Array} syncLogs
 * @returns {Object}
 */
export function computeSuccessRates(syncLogs) {
  if (!syncLogs || syncLogs.length === 0) {
    return { overall: 100, byType: {} };
  }
  
  const totalSuccess = syncLogs.filter(l => l.result === 'success').length;
  const overall = (totalSuccess / syncLogs.length * 100);
  
  // Group by type
  const byType = {};
  const typeGroups = {};
  
  for (const log of syncLogs) {
    if (!typeGroups[log.type]) {
      typeGroups[log.type] = { success: 0, total: 0 };
    }
    typeGroups[log.type].total++;
    if (log.result === 'success') {
      typeGroups[log.type].success++;
    }
  }
  
  for (const [type, counts] of Object.entries(typeGroups)) {
    byType[type] = (counts.success / counts.total * 100);
  }
  
  return { overall, byType };
}

export default {
  RELIABILITY_STATUS,
  RELIABILITY_STATUS_CONFIG,
  createEmptyReliabilitySummary,
  buildReliabilitySummary,
  computeReliabilityStatus,
  computeAverageResolutionTime,
  computeSuccessRates
};
